import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Verdict from '../components/verdict'

import { Link, Pagination, Table, TableBody, TableHead, TableRow } from '@airbus/components-react/components'
import LangContext from 'context/LangContext'
import URL_APIS from 'utils/api/urlApis'
import ComponentsUNames from 'constants/componentsUNames'
import LocalStorage from 'utils/myLocalStorage'
import useQuery from 'utils/hooks/useQuery'
import ResponsiveTableCell from 'components/responsiveTableCell'
import config from 'constants/config'

const WidgetHIL = ({ pageUniqueName, selectedAircraft }) => {
  const { t } = useTranslation()

  const { lang } = useContext(LangContext)

  // Page for main aircrafts' widget
  const [searchParams] = useSearchParams()
  const jumpToPage = Number(searchParams.get('page')) || 0

  const hasWoPermissions = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, 'AMSWEB_ACCESS_MAOE-DSP'],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const { fetchData: fetchHIL } = useQuery(URL_APIS.URL_FLEETPAN_HIL_GET)

  const [pagination, setPagination] = useState({
    nbItems: 0,
    pageSize: LocalStorage.getOrDefault(
      [
        LocalStorage.KEY_PREFS,
        ComponentsUNames.TABFLOT_STORE_NAME,
        ComponentsUNames.TABFLOT_WDG_HIL_UNAME + '|' + ComponentsUNames.PROP_PAGE_SIZE,
      ],
      LocalStorage.VALUE_TYPE.INTEGER,
      config.PAGINATION_DEFAULT_PAGESIZE,
    ),
    pageNo: 0,
    nbPages: 0,
    choiceNbRows: [10, 20],
  })

  const [data, setData] = useState()
  const [isLoadInProgressActive, setIsLoadInProgressActive] = useState(false)

  const navigate = useNavigate()

  /********
   * Synchronization of widget's data to keep track of them
   ********/

  const loadData = async () => {
    setIsLoadInProgressActive(true)
    const response = await fetchHIL({
      lang,
      avimmatr: selectedAircraft,
      size: pagination.pageSize,
      page: pagination.pageNo,
    })
    setIsLoadInProgressActive(false)
    setData(response?.data.content)

    setPagination({
      ...pagination,
      nbItems: response?.data.totalElements,
      nbPages: response?.data.totalPages,
      pageNo: response?.data.number,
    })
  }

  // Sync when changing aircraft selection
  useEffect(() => {
    if (jumpToPage) {
      setData([])
    } else if (selectedAircraft !== undefined) {
      loadData()
    }
  }, [selectedAircraft, lang, pagination.pageSize, pagination.pageNo, jumpToPage])

  return (
    <div className='WidgetHIL'>
      {data && JSON.stringify(data) !== '{}' && JSON.stringify(data) !== '[]' ? (
        <div>
          <Table size='small'>
            <colgroup>
              <col style={{ width: '2%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '6%' }} />
              <col style={{ width: '6%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <ResponsiveTableCell head></ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500025')}</ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500027')}</ResponsiveTableCell>
                <ResponsiveTableCell head style={{ minWidth: '4em' }}>
                  {t('1MOE048')}
                </ResponsiveTableCell>
                <ResponsiveTableCell head style={{ width: '15em' }}>
                  {t('9500081')}
                </ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500028')}</ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500029')}</ResponsiveTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data ? (
                data.map((d, i) => (
                  <TableRow id={d.wo_tfu} key={'line_hil_' + i} style={{ cursor: 'pointer' }}>
                    <ResponsiveTableCell align='center'>
                      <Verdict value={d.hasOwnProperty('verdict') ? d.verdict : ''} />
                    </ResponsiveTableCell>
                    <ResponsiveTableCell>
                      {d?.woTfu && (
                        <Link
                          size='small'
                          variant='underlined'
                          onClick={() => navigate(d.woTfu)}
                          disabled={!hasWoPermissions}
                        >
                          {d.woTfu}
                        </Link>
                      )}
                    </ResponsiveTableCell>
                    <ResponsiveTableCell>{d.hasOwnProperty('codeMel') ? d.codeMel : '-'}</ResponsiveTableCell>
                    <ResponsiveTableCell>{d.hasOwnProperty('itemMel') ? d.itemMel : '-'}</ResponsiveTableCell>
                    <ResponsiveTableCell>{d.hasOwnProperty('labelMel') ? d.labelMel : '-'}</ResponsiveTableCell>
                    <ResponsiveTableCell>{d.hasOwnProperty('dateOpening') ? d.dateOpening : '-'}</ResponsiveTableCell>
                    <ResponsiveTableCell>{d.hasOwnProperty('dateDeadline') ? d.dateDeadline : '-'}</ResponsiveTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow />
              )}
            </TableBody>

            <TableRow>
              <ResponsiveTableCell colSpan='7'>
                <Pagination
                  count={pagination.nbItems}
                  page={pagination.pageNo + 1}
                  onPageChange={(e, newPage) => setPagination({ ...pagination, pageNo: newPage - 1 })}
                  rowsPerPage={pagination.pageSize}
                  onRowsPerPageChange={(e, newRowPerPage) => {
                    LocalStorage.put(
                      [LocalStorage.KEY_PREFS, pageUniqueName, ComponentsUNames.TABFLOT_WDG_HIL_UNAME + '|PAGESIZE'],
                      newRowPerPage,
                    )
                    setPagination({ ...pagination, pageSize: parseInt(newRowPerPage), pageNo: 0 })
                  }}
                  rowsPerPageOptions={pagination.choiceNbRows}
                  showRowsPerPage
                  rowsPerPageLabel={t('entries_per_page')}
                />
              </ResponsiveTableCell>
            </TableRow>
          </Table>
        </div>
      ) : (
        <div style={{ paddingLeft: '1em' }}>{isLoadInProgressActive ? t('9500166') : t('9500055')}</div>
      )}
    </div>
  )
}

export default WidgetHIL
